import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Link from './Link/Link';
import Standard from 'client/components/aria/Button/Standard/Standard';
import Toggle from './Toggle/Toggle';
import Tooltip from 'client/components/aria/Tooltip/Tooltip';
import defaultAriaProps from 'client/utils/constants/ariaPropTypes';

import styles from './Button.module.scss';
export const BUTTON_VARIANTS = ['default', 'primary', 'secondary', 'error', 'warning', 'success', 'info', 'transparent', 'link', 'unstyled'];

// Tracks close handlers of all currently open tooltip buttons, so opening one tooltip closes any others still showing
const openTooltipClosers = new Set();

// eslint-disable-next-line react/display-name
const Button = forwardRef((
    { children, href, to, onChange, tooltip, onPress, ...props }, ref
) => {
    let component;

    const defaultRef = useRef();
    const buttonRef = ref || defaultRef;
    const wrapperRef = useRef();
    const [showTooltip, setShowTooltip] = useState(false);

    const closeTooltip = useCallback(() => {
        openTooltipClosers.delete(closeTooltip);
        setShowTooltip(false);
    }, []);

    useEffect(() => {
        return () => openTooltipClosers.delete(closeTooltip);
    }, [closeTooltip]);

    const openTooltip = () => {
        openTooltipClosers.forEach(close => close !== closeTooltip && close());
        openTooltipClosers.add(closeTooltip);
        setShowTooltip(true);
    };

    if (href || to) {
        component = <Link ref={buttonRef} href={href} to={to} {...props}>{children}</Link>;
    } else {
        component = onChange
            ? <Toggle ref={buttonRef} onChange={onChange} onPress={onPress} {...props}>{children}</Toggle>
            : <Standard ref={buttonRef} onPress={onPress} {...props}>{children}</Standard>;
    }

    if (tooltip) {
        return (
            <span
                className={styles.tooltipWrapper}
                ref={wrapperRef}
                onMouseOver={openTooltip}
                onMouseOut={closeTooltip}
            >
                {component}
                <Tooltip
                    isOpen={showTooltip}
                    onClose={closeTooltip}
                    triggerRef={wrapperRef}
                    data-testid={props['data-testid'] ? `${props['data-testid']}.tooltip` : null}
                >
                    {tooltip}
                </Tooltip>
            </span>
        );
    }

    return (component);
});

Button.propTypes = {
    ...defaultAriaProps,
    className: PropTypes.string,
    href: PropTypes.string,
    to: PropTypes.string,
    isSelected: PropTypes.bool,
    defaultSelected: PropTypes.bool,
    isDisabled: PropTypes.bool,
    onChange: PropTypes.func,
    onPress: PropTypes.func,
    tooltip: PropTypes.node,
    title: PropTypes.string,
    variant: PropTypes.oneOf(BUTTON_VARIANTS),
    elementType: PropTypes.oneOf(['button', 'span', 'div', 'a'])
};

Toggle.defaultProps = {
    variant: 'default',
    elementType: 'span'
};

export default Button;
